import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router'

import { useAuthStore } from '@/stores/auth/auth.store.js'

/**
 * The global guard: **session classification only**.
 *
 * It answers one question — may this visitor see an authenticated screen at all — and
 * deliberately not "may they see *this* screen". Permissions are enforced by each route's
 * own `beforeEnter`, built by `requirePermissions` below, because a central
 * path-to-permission map here would be a second copy of the route table and would drift
 * from it silently.
 *
 * ## The ordering that matters
 *
 * `await store.ready` **first**. Hydration is a network round trip, and on a cold load the
 * first navigation starts before it finishes. Guarding without waiting means `operator` is
 * still `null`, every protected route redirects to `/login`, and a signed-in operator who
 * opens a bookmark lands on the sign-in screen — the single most common defect in a
 * cookie-session SPA, and one that looks like a backend fault.
 */
export function sessionGuard(): NavigationGuardWithThis<undefined> {
  return async (to) => {
    const auth = useAuthStore()

    // `hydrate()` is kicked off by bootstrap.ts; this waits for whichever attempt is in
    // flight rather than starting a second one.
    if (!auth.ready) await auth.hydrate()

    if (to.meta.public === true) {
      // A signed-in operator has no use for the login screen. Sending them to the panel
      // instead of rendering it avoids the state where signing in again rotates their
      // session for no reason.
      return auth.isAuthenticated && to.name === 'login' ? { name: 'overview' } : true
    }

    if (!auth.isAuthenticated) return toLogin(to)

    /**
     * The password lock, mirrored.
     *
     * The backend enforces this regardless — every admin route but two answers 403 until
     * the flag clears — so this redirect is the UX. Without it the operator sees a shell
     * full of 403s and no explanation of what to do.
     */
    if (auth.mustChangePassword && to.name !== 'change-password') {
      return { name: 'change-password' }
    }

    return true
  }
}

/**
 * A route's own permission check.
 *
 * Attached as `beforeEnter` by the route that needs it, so the requirement sits in the same
 * object as the path it protects. `meta.requires` is typed as `PermissionName[]`, so a typo
 * fails the build rather than becoming a permission nobody holds.
 *
 * **All listed permissions are required, not any.** A screen that reads two resources needs
 * both; "any" would render it with half its panels 403ing.
 */
export function requirePermissions(): NavigationGuardWithThis<undefined> {
  return (to) => {
    const required = to.meta.requires ?? []

    if (required.length === 0) return true

    const auth = useAuthStore()

    if (auth.canAll(required)) return true

    // Not a redirect to the login screen: the operator is signed in and their session is
    // fine. Sending them to sign in again would be a loop that cannot resolve, because a
    // second sign-in grants no new permission.
    return { name: 'forbidden', query: { from: to.fullPath } }
  }
}

/**
 * Where to go when a visitor is not signed in.
 *
 * `fullPath` so a deep link survives the round trip — query and hash included, because a
 * filtered list URL is the thing an operator pastes to a colleague. `safeRedirect`
 * validates it on the way back out; it is attacker-controlled by then.
 */
function toLogin(to: RouteLocationNormalized): { name: string; query: { redirect: string } } {
  return { name: 'login', query: { redirect: to.fullPath } }
}
